import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { LangProvider } from './context/LangContext';

import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import './styles/global.css';

// Eager — critical path
import Home from './pages/Home';
import { LoginPage, RegisterPage } from './pages/Auth';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/ForgotPassword';
import Cart from './pages/Cart';

// Lazy — rest
const Shop           = lazy(() => import('./pages/Shop'));
const ProductDetail  = lazy(() => import('./pages/ProductDetail'));
const Astrology      = lazy(() => import('./pages/Astrology'));
const Admin          = lazy(() => import('./pages/Admin'));
const Profile        = lazy(() => import('./pages/Profile'));

const LazyNamed = ({ name }) => {
  const [C, setC] = React.useState(null);
  React.useEffect(() => { import('./pages/OtherPages').then(m => setC(() => m[name])); }, [name]);
  return C ? <C /> : <PageLoader />;
};

function PageLoader() {
  return (
    <div style={{ minHeight:'60vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div><div className="spinner"/><p style={{ textAlign:'center', color:'var(--text-muted)', fontSize:'.82rem', marginTop:'.5rem', fontFamily:'var(--font-devanagari)' }}>लोड हो रहा है…</p></div>
    </div>
  );
}

function ProtectedRoute({ children, adminOnly = false }) {
  const { isAuthenticated, user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (adminOnly && user?.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

function GuestRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (isAuthenticated) return <Navigate to="/" replace />;
  return children;
}

function WhatsApp() {
  return (
    <a href="https://wa.me/919876543210?text=नमस्ते! मुझे राघवेंद्र पूजन भंडार से जानकारी चाहिए।" target="_blank" rel="noreferrer"
      style={{ position:'fixed', bottom:24, right:24, zIndex:999, background:'#25D366', color:'#fff', borderRadius:'50%', width:54, height:54, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.6rem', textDecoration:'none', boxShadow:'0 4px 20px rgba(37,211,102,.45)', transition:'transform .2s' }}
      onMouseEnter={e=>e.currentTarget.style.transform='scale(1.1)'} onMouseLeave={e=>e.currentTarget.style.transform='scale(1)'}
      title="WhatsApp Support">💬</a>
  );
}

function AppLayout({ children }) {
  return (
    <>
      <Navbar/>
      <main style={{ minHeight:'60vh' }}><Suspense fallback={<PageLoader/>}>{children}</Suspense></main>
      <Footer/>
      <WhatsApp/>
    </>
  );
}

function NotFound() {
  return (
    <div style={{ textAlign:'center', padding:'5rem 1rem', minHeight:'60vh' }}>
      <div style={{ fontSize:'4rem', marginBottom:'1rem' }}>🛕</div>
      <h1 style={{ fontFamily:'var(--font-devanagari)', color:'var(--maroon)', fontSize:'1.8rem', marginBottom:'.5rem' }}>404 — पृष्ठ नहीं मिला</h1>
      <p style={{ color:'var(--text-muted)', marginBottom:'1.5rem' }}>Page not found</p>
      <a href="/" className="btn btn-primary" style={{ display:'inline-flex' }}>🏠 होम पर जाएं</a>
    </div>
  );
}

function AppRoutes() {
  return (
    <AppLayout>
      <Routes>
        {/* Public */}
        <Route path="/"                   element={<Home/>}/>
        <Route path="/shop"               element={<Shop/>}/>
        <Route path="/product/:slug"      element={<ProductDetail/>}/>
        <Route path="/astrology"          element={<Astrology/>}/>
        <Route path="/rashifal"           element={<LazyNamed name="RashifalPage"/>}/>
        <Route path="/panchang"           element={<LazyNamed name="PanchangPage"/>}/>
        <Route path="/pandit-booking"     element={<LazyNamed name="PanditBookingPage"/>}/>
        <Route path="/cart"               element={<Cart/>}/>
        <Route path="/track-order"        element={<LazyNamed name="TrackOrderPage"/>}/>

        {/* Guest only */}
        <Route path="/login"                      element={<GuestRoute><LoginPage/></GuestRoute>}/>
        <Route path="/register"                   element={<GuestRoute><RegisterPage/></GuestRoute>}/>
        <Route path="/forgot-password"            element={<GuestRoute><ForgotPasswordPage/></GuestRoute>}/>
        <Route path="/reset-password/:token"      element={<GuestRoute><ResetPasswordPage/></GuestRoute>}/>

        {/* Protected */}
        <Route path="/profile"            element={<ProtectedRoute><Profile/></ProtectedRoute>}/>
        <Route path="/wishlist"           element={<ProtectedRoute><Profile tab="wishlist"/></ProtectedRoute>}/>
        <Route path="/my-orders"          element={<ProtectedRoute><LazyNamed name="MyOrdersPage"/></ProtectedRoute>}/>
        <Route path="/order-success/:id"  element={<ProtectedRoute><LazyNamed name="OrderSuccessPage"/></ProtectedRoute>}/>

        {/* Admin */}
        <Route path="/admin"  element={<ProtectedRoute adminOnly><Admin/></ProtectedRoute>}/>
        <Route path="/admin/*" element={<ProtectedRoute adminOnly><Admin/></ProtectedRoute>}/>

        {/* 404 */}
        <Route path="*" element={<NotFound/>}/>
      </Routes>
    </AppLayout>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <LangProvider>
        <AuthProvider>
          <CartProvider>
            <AppRoutes/>
            <ToastContainer
              position="top-right" autoClose={3500} hideProgressBar={false}
              newestOnTop closeOnClick pauseOnHover theme="dark"
              toastStyle={{ background:'var(--maroon-deep)', border:'1px solid rgba(201,148,10,.35)', color:'var(--gold-light)', fontFamily:'var(--font-body)', fontSize:'.85rem' }}
            />
          </CartProvider>
        </AuthProvider>
      </LangProvider>
    </BrowserRouter>
  );
}
